import { useCallback, useEffect } from 'react';

const RECAPTCHA_SCRIPT_ID = 'google-recaptcha-v3';
const RECAPTCHA_READY_TIMEOUT_MS = 10000;

const cleanupRecaptchaNodes = () => {
  document.querySelectorAll('.grecaptcha-badge').forEach((node) => {
    node.remove();
  });

  document
    .querySelectorAll('iframe[src*="recaptcha"], iframe[src*="google.com/recaptcha"]')
    .forEach((node) => {
      node.remove();
    });
};

const waitForRecaptcha = () =>
  new Promise((resolve, reject) => {
    const startedAt = Date.now();

    const check = () => {
      if (window.grecaptcha?.execute) {
        resolve(window.grecaptcha);
        return;
      }

      if (Date.now() - startedAt > RECAPTCHA_READY_TIMEOUT_MS) {
        reject(new Error('reCAPTCHA failed to load. Please refresh and try again.'));
        return;
      }

      window.setTimeout(check, 100);
    };

    check();
  });

const useRecaptcha = () => {
  const siteKey = process.env.REACT_APP_RECAPTCHA_SITE_KEY;

  useEffect(() => {
    if (!siteKey) {
      console.error('RECAPTCHA site key is missing. Check Docker build args or environment variables.');
      return undefined;
    }

    if (!document.getElementById(RECAPTCHA_SCRIPT_ID)) {
      const script = document.createElement('script');
      script.id = RECAPTCHA_SCRIPT_ID;
      script.src = `https://www.google.com/recaptcha/api.js?render=${siteKey}`;
      script.async = true;
      script.defer = true;
      document.body.appendChild(script);
    }

    return () => {
      document.getElementById(RECAPTCHA_SCRIPT_ID)?.remove();
      cleanupRecaptchaNodes();
    };
  }, [siteKey]);

  const executeRecaptcha = useCallback(
    async (action) => {
      if (!siteKey) {
        throw new Error('reCAPTCHA site key is missing.');
      }

      const grecaptcha = await waitForRecaptcha();
      return grecaptcha.execute(siteKey, { action });
    },
    [siteKey]
  );

  return { executeRecaptcha, siteKey };
};

export default useRecaptcha;
